import cn from "classnames";
import HDSingleSelect from "@/common/components/HDSingleSelect";
import { PHONE_COUNTRY_CODES } from "@/common/utils/phone";
import { isExtensionValid } from "@/features/phone-call-widget/callerIdExtension";
import DialSequenceField from "@/features/phone-call-widget/DialSequenceField";
import type { DialStep } from "@/features/phone-call-widget/dialSequence";
import AlertTriangleIcon from "@icons/alert-triangle.svg?react";

export type PhoneNumberFormVariant = "light" | "dark";

export interface PhoneNumberFormFieldsProps {
    /** `light` = settings/modal styling; `dark` = dialer-widget styling. */
    variant: PhoneNumberFormVariant;
    /**
     * Namespace for this form's input ids: `{idPrefix}-phone-input`, `-country-code`,
     * `-reference-input`, plus `-extension-input` on destination surfaces only (caller-ID surfaces
     * render `DialSequenceField` instead of the plain extension input, and it namespaces its own
     * ids with `useId`).
     *
     * Must be unique per mounted form: the ids carry the `label htmlFor` -> input association, so two
     * mounted forms sharing a prefix give the second form's inputs no accessible name, and clicking
     * its label focuses the first form's input.
     */
    idPrefix: string;

    countryCodeKey: string;
    onCountryCodeChange: (key: string) => void;
    phoneNumber: string;
    onPhoneNumberChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    /** Optional ref for the phone number input (e.g. for focus on mount) */
    phoneInputRef?: React.RefObject<HTMLInputElement | null>;
    /** Locks the number and country inputs (e.g. retrying verification on an existing row). */
    disabled?: boolean;

    referenceName: string;
    onReferenceNameChange: (value: string) => void;
    referenceNamePlaceholder: string;

    /** Extension is optional per call site; when hidden, the number spans the full row. */
    showExtension?: boolean;
    /** Destination-surface only (see `isCallerIdSurface`) — the plain comma-syntax text value. */
    extension?: string;
    onExtensionChange?: (value: string) => void;
    /**
     * Caller-ID-surface only (see `isCallerIdSurface`) — the structured dial sequence backing the
     * `DialSequenceField` step builder that replaces the plain extension input there. The caller
     * owns entry-state resolution (preferring a stored `dialSteps` over `parse(extension)` — see
     * `dialSequence.ts`) and receives every edit back through `onDialStepsChange`; this component
     * just threads the value through to `DialSequenceField` unmodified.
     */
    dialSteps?: DialStep[];
    onDialStepsChange?: (steps: DialStep[]) => void;

    /**
     * True when this form is verifying a caller ID (vs. adding a staff destination). Drives the
     * "be at this phone" expectation callout (EN-1050): Twilio's verification call is answered 92%
     * of the time and then times out because whoever clicked Save & Verify isn't standing at the
     * phone that's about to ring. Staff-destination numbers are never called for verification, so
     * the callout would be false there and must stay off.
     *
     * Also drives which extension control renders (EN-1050 Task 8): a caller-ID surface renders
     * the `DialSequenceField` step builder (`dialSteps`/`onDialStepsChange`) in place of the plain
     * extension text input (`extension`/`onExtensionChange`) — the two features share this one
     * prop instead of two independent booleans that would have to be kept in agreement by hand.
     */
    isCallerIdSurface?: boolean;
}

/**
 * The phone-number form shared by every add/edit surface: country + number, extension, and
 * reference name. Four surfaces used to hand-roll these inputs and drifted apart on placeholders
 * and field order; they all render this now, so the only intentional differences left are the ones
 * `variant` encodes.
 */
function PhoneNumberFormFields({
    variant,
    idPrefix,
    countryCodeKey,
    onCountryCodeChange,
    phoneNumber,
    onPhoneNumberChange,
    phoneInputRef,
    disabled = false,
    referenceName,
    onReferenceNameChange,
    referenceNamePlaceholder,
    showExtension = true,
    extension = "",
    onExtensionChange,
    dialSteps,
    onDialStepsChange,
    isCallerIdSurface = false,
}: PhoneNumberFormFieldsProps) {
    const isLight = variant === "light";

    const phoneNumberInputId = `${idPrefix}-phone-input`;
    const countrySelectId = `${idPrefix}-country-code`;
    const extensionInputId = `${idPrefix}-extension-input`;
    const referenceNameInputId = `${idPrefix}-reference-input`;

    const labelClass = cn("text-sm font-medium", isLight ? "text-gray-700" : "text-white");
    const optionalClass = cn("font-normal", isLight ? "text-gray-500" : "text-gray-400");
    const errorClass = cn("text-sm", isLight ? "mt-1 text-red-600" : "text-red-400");

    const rowGapClass = cn("flex flex-col", !isLight && "gap-1.5");

    const phoneRowWrapperClass = cn(
        "flex rounded-md overflow-hidden",
        isLight ? "mt-1 border border-gray-200" : "bg-white",
    );

    const phoneInputClass = cn(
        "flex-1 border-0 placeholder:text-gray-400 focused-inset",
        // A locked field still holds a real number, so it must not be dimmed to the placeholder's
        // own gray-400 — that made a populated field read as empty. Grayed background plus
        // gray-700 text says "not editable" while keeping the value legible.
        disabled ? "text-gray-700 bg-gray-100 cursor-not-allowed" : "text-gray-900",
        isLight ? "rounded-none px-4 py-2" : "px-3 py-2 text-sm",
    );

    /** Extension and reference name share this styling; the number row is built from parts. */
    const textInputClass = isLight
        ? "mt-1 rounded-md border border-gray-200 px-4 py-2 text-gray-900 focused-inset"
        : "w-full px-3 py-2 bg-white rounded-md border-0 text-gray-900 text-sm placeholder:text-gray-400 focused-inset";

    const selectCustomClass = "w-[4.75rem] min-w-[4.75rem] flex-shrink-0 border-0 rounded-none rounded-l-md";

    const selectInputClass = cn("ring-white shadow-none rounded-none rounded-l-md border-0 border-r-0", {
        "bg-white": !isLight,
    });

    const numberField = (
        <div className={rowGapClass}>
            <label htmlFor={phoneNumberInputId} className={labelClass}>
                Phone Number
            </label>
            <div className={phoneRowWrapperClass}>
                <HDSingleSelect
                    id={countrySelectId}
                    options={Object.keys(PHONE_COUNTRY_CODES)}
                    value={countryCodeKey}
                    onChange={(key) => onCountryCodeChange(key)}
                    placeholder="Country"
                    customClassNames={selectCustomClass}
                    inputClassName={cn("p-2", selectInputClass)}
                    disabled={disabled}
                />
                <input
                    ref={phoneInputRef as React.RefObject<HTMLInputElement>}
                    id={phoneNumberInputId}
                    type="tel"
                    value={phoneNumber}
                    onChange={onPhoneNumberChange}
                    placeholder="(123) 456-7890"
                    className={phoneInputClass}
                    disabled={disabled}
                />
            </div>
            {disabled && (
                <p className={cn("text-sm", isLight ? "mt-1 text-gray-500" : "text-gray-400")}>
                    Retrying this number. To verify a different one, use Add Number.
                </p>
            )}
        </div>
    );

    // Staff-destination surfaces only: caller-ID surfaces render `dialSequenceBuilder` below
    // instead. So `capped` is unconditionally true here — Twilio caps `sendDigits` at 32 characters
    // on a live call, and that is the only path left that renders this plain text input. Caller-ID
    // verification goes through `validationRequests`, which has no such limit.
    const extensionField = (
        <div className={rowGapClass}>
            <label htmlFor={extensionInputId} className={labelClass}>
                Extension <span className={optionalClass}>(optional)</span>
            </label>
            <input
                id={extensionInputId}
                type="text"
                value={extension}
                onChange={(e) => onExtensionChange?.(e.target.value)}
                placeholder="e.g. 2,101"
                className={textInputClass}
            />
            {/* Charset/at-least-one-symbol rule applies regardless of cap, so checking uncapped isolates it
                from the length rule below — an over-long-but-otherwise-valid sequence must not also trip
                this message. */}
            {extension && !isExtensionValid(extension, { capped: false }) && (
                <p className={errorClass}>Extension may only contain digits (0–9), *, #, and commas.</p>
            )}
            {/* Only shown when the charset is already valid, so the two messages never stack. */}
            {extension &&
                isExtensionValid(extension, { capped: false }) &&
                !isExtensionValid(extension, { capped: true }) && (
                    <p className={errorClass}>
                        This sequence is too long. It can total up to 32 dialed characters, and each comma counts as 4.
                    </p>
                )}
        </div>
    );

    // Caller-ID surfaces only: the phone-tree step builder in place of the plain extension input.
    // No error copy of its own — not because there is nothing to reject (the builder constrains the
    // characters it emits via `sanitizeDigits`/`WAIT_OPTIONS`, but not the size), but because
    // `DialSequenceField` renders its own message from `dialSequenceProblem`, the same call the
    // parent forms use to gate Save. One source, so the button and the message cannot disagree.
    const dialSequenceBuilder = (
        <div className={rowGapClass}>
            <p className={labelClass}>
                Extension <span className={optionalClass}>(optional)</span>
            </p>
            <DialSequenceField
                value={dialSteps ?? []}
                onChange={(steps) => onDialStepsChange?.(steps)}
                variant={variant}
            />
        </div>
    );

    // Static: no Tooltip, no openEvents, no dismissal. This is deliberately NOT the extension
    // field's hover/focus tooltip pattern — that pattern is exactly what fails these users, since
    // nobody hovers or focuses anything before a phone rings unattended at the front desk. Gold
    // (tropicana), not red: this is an instruction, not an error. Both variants render the same
    // copy even though the dialer's narrow column wraps it — one string that can't drift beats two
    // that can.
    const expectationCallout = isCallerIdSurface && (
        <div
            className={cn(
                "flex items-start gap-2 rounded-md border px-3 py-2.5",
                isLight
                    ? "border-tropicana-200 bg-tropicana-25 text-tropicana-900"
                    : "border-tropicana-500/30 bg-tropicana-500/10 text-tropicana-300",
            )}
        >
            <AlertTriangleIcon
                className={cn("h-4 w-4 mt-0.5 shrink-0", isLight ? "text-tropicana-600" : "text-tropicana-400")}
                aria-hidden="true"
            />
            <p className="text-sm">
                <span className="font-semibold">Be at this phone before you continue.</span> We&rsquo;ll call it in a
                few seconds, and someone has to type a 6-digit code on that handset.
            </p>
        </div>
    );

    const referenceNameField = (
        <div className={rowGapClass}>
            <label htmlFor={referenceNameInputId} className={labelClass}>
                Reference Name
            </label>
            <input
                id={referenceNameInputId}
                type="text"
                value={referenceName}
                onChange={(e) => onReferenceNameChange(e.target.value)}
                placeholder={referenceNamePlaceholder}
                className={textInputClass}
            />
        </div>
    );

    return (
        <div className="flex flex-col gap-4">
            {/* The dialer widget is a narrow fixed-width column, so only the light variant has room
                to put the extension beside the number. Both orders keep number → extension →
                reference name, which is the order the fields are filled in.
                Caller-ID surfaces (the step builder) always stack full-width regardless of
                variant — the narrow `lg:w-36` side-by-side column was sized for a single text
                input, not the builder's dropdowns and rows. */}
            {showExtension ? (
                isCallerIdSurface ? (
                    <>
                        {numberField}
                        {dialSequenceBuilder}
                    </>
                ) : isLight ? (
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                        <div className="flex-1">{numberField}</div>
                        <div className="flex flex-col lg:w-36">{extensionField}</div>
                    </div>
                ) : (
                    <>
                        {numberField}
                        {extensionField}
                    </>
                )
            ) : (
                numberField
            )}
            {referenceNameField}
            {/* The callout comes LAST, immediately above the footer buttons, and its position is
                load-bearing rather than aesthetic (Design Decision 01, and the reading order in §8.3
                of the design: what you built → what will happen → go).

                Putting it first is the tempting alternative and it is wrong: CallerIdNumberForm's
                body is a scrollport (`flex-1 min-h-0 overflow-y-auto`), so a callout at the TOP
                scrolls out of view exactly when an expanded builder pushes the vet down to Save &
                Verify — precisely when the copy has to be on screen. Last keeps it adjacent to the
                button it qualifies. */}
            {expectationCallout}
        </div>
    );
}

export default PhoneNumberFormFields;
